import { useMemo, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { notifySuccess } from 'app/services/notify.client'
import { useOpsPromptsData, type PromptDraft } from './OpsPromptsContext'
import type { Prompt, PromptVersion } from './mockData'

export type { PromptDraft }

export type UsePromptsPanelResult = {
  prompts: Prompt[]
  /** Undefined when there are no prompts. */
  selected: Prompt | undefined
  selectedId: string
  draft: PromptDraft | undefined
  isDirty: boolean
  /** True when the draft version is already the active one (nothing to activate). */
  isDraftLive: boolean
  /** ISO timestamp of the last local save, or null. */
  lastSavedAt: string | null
  /** Versions of the selected prompt, newest first (drives the history column). */
  versions: PromptVersion[]
  /** Version currently open in the detail sheet, or null. */
  viewing: PromptVersion | null
  /** Body of the version before `viewing`, for the diff; null if it's the first. */
  viewingPrevBody: string | null
  onSelect: (id: string) => void
  onDraftChange: (patch: Partial<PromptDraft>) => void
  onSave: () => void
  onActivate: () => void
  onReset: () => void
  onViewVersion: (version: PromptVersion) => void
  onCloseView: () => void
}

/**
 * Prompts page state. The prompt library + mutations live in the shared
 * `OpsPromptsProvider`; this hook keeps the panel's ephemeral selection/draft
 * state and derives the draft-vs-active version model for the editor + history.
 */
export function usePromptsPanel(): UsePromptsPanelResult {
  const { prompts, versions: allVersions, savePrompt, activateDraft } = useOpsPromptsData()
  const [confirm] = useConfirmation()
  // Empty string means "fall back to the first prompt".
  const [selectedId, setSelectedId] = useState<string>(``)
  const [draftById, setDraftById] = useState<Record<string, PromptDraft>>({})
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [viewing, setViewing] = useState<PromptVersion | null>(null)

  const selected = useMemo(
    () => prompts.find((prompt) => prompt.id === selectedId) ?? prompts[0],
    [prompts, selectedId],
  )

  // Versions of the selected prompt, newest first.
  const versions = useMemo(
    () =>
      selected
        ? allVersions.filter((version) => version.promptId === selected.id).sort((a, b) => b.version - a.version)
        : [],
    [allVersions, selected],
  )

  const draftBody = useMemo(
    () => versions.find((version) => version.version === selected?.draftVersion)?.body ?? ``,
    [versions, selected],
  )

  const base: PromptDraft | undefined = selected
    ? { name: selected.name, description: selected.description, body: draftBody }
    : undefined

  const draft: PromptDraft | undefined = selected ? (draftById[selected.id] ?? base) : undefined

  const isDirty =
    base != null &&
    draft != null &&
    (draft.name !== base.name || draft.description !== base.description || draft.body !== base.body)

  const viewingPrevBody = useMemo(() => {
    if (!viewing) return null
    const previous = allVersions
      .filter((version) => version.promptId === viewing.promptId && version.version < viewing.version)
      .sort((a, b) => b.version - a.version)[0]
    return previous?.body ?? null
  }, [allVersions, viewing])

  const onSelect = (id: string) => {
    setSelectedId(id)
    setLastSavedAt(null)
  }

  const onDraftChange = (patch: Partial<PromptDraft>) => {
    if (!selected || !draft) return
    setDraftById((prev) => ({ ...prev, [selected.id]: { ...draft, ...patch } }))
  }

  const clearDraft = (id: string) =>
    setDraftById((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })

  const onReset = () => {
    if (!selected) return
    clearDraft(selected.id)
    setLastSavedAt(null)
  }

  const onSave = () => {
    if (!selected || !draft) return
    savePrompt(selected.id, draft)
    clearDraft(selected.id)
    setLastSavedAt(new Date().toISOString())
  }

  const onActivate = () => {
    if (!selected) return
    const activatedId = selected.id
    const draftVersion = selected.draftVersion
    confirm({
      variant: `info`,
      title: `Activate version`,
      description: `Make v${draftVersion} of "${selected.name}" the active version the pipeline uses? This is a frontend-only demo — nothing is persisted.`,
      submitLabel: `Activate`,
    })
      .then(() => {
        activateDraft(activatedId)
        notifySuccess(`Version activated`)
      })
      .catch(() => {
        // cancelled — leave the active version in place
      })
  }

  return {
    prompts,
    selected,
    selectedId: selected?.id ?? ``,
    draft,
    isDirty,
    isDraftLive: selected != null && selected.draftVersion === selected.activeVersion,
    lastSavedAt,
    versions,
    viewing,
    viewingPrevBody,
    onSelect,
    onDraftChange,
    onSave,
    onActivate,
    onReset,
    onViewVersion: (version: PromptVersion) => setViewing(version),
    onCloseView: () => setViewing(null),
  }
}
